import { type AdPlatformConversion } from '@/lib/analytics';

// No translation here: main.tsx imports it, and a t() would charge every route a namespace.
export const OAUTH_CHANNEL = 'oauth_channel';
export const MSG_TYPE = 'PLATFORM_CONNECTION_COMPLETE';
export const OAUTH_POPUP_FLAG = 'is_oauth_popup';
// The opener writes it into the popup's sessionStorage, so every page the popup lands on can name its flow.
export const OAUTH_FLOW_KEY = 'oauth_flow';
// The opener's own sessionStorage: a reload while the picker is open still counts the result.
const STARTED_FLOWS_KEY = 'oauth_started_flows';
const STARTED_FLOWS_KEPT = 20;

export type OAuthStatus = 'success' | 'error' | 'select_account';

export interface OAuthFlow {
  organizationId: number | null;
  nonce: string;
}

export interface OAuthMessage extends Partial<OAuthFlow> {
  type: string;
  status: OAuthStatus;
  message?: string | null;
  // Present only when the backend claimed the organization's first link of the platform.
  conversion?: AdPlatformConversion;
}

const readPopupFlow = (): Partial<OAuthFlow> => {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_FLOW_KEY);
    return raw === null ? {} : (JSON.parse(raw) as OAuthFlow);
  } catch {
    return {};
  }
};

// No nonce: a popup with no stamp (opened by the previous release, or storage blocked) settles as before.
export const isFlowResult = (
  data: OAuthMessage,
  flow: OAuthFlow | null | undefined,
): boolean =>
  data.nonce === undefined ||
  (data.nonce === flow?.nonce && data.organizationId === flow.organizationId);

const startedFlows = new Map<string, OAuthFlow>();

const readStartedFlows = (): OAuthFlow[] => {
  try {
    const raw = window.sessionStorage.getItem(STARTED_FLOWS_KEY);
    return raw === null ? [] : (JSON.parse(raw) as OAuthFlow[]);
  } catch {
    return [];
  }
};

export const rememberFlow = (flow: OAuthFlow): void => {
  startedFlows.set(flow.nonce, flow);
  try {
    const kept = [...readStartedFlows(), flow].slice(-STARTED_FLOWS_KEPT);
    window.sessionStorage.setItem(STARTED_FLOWS_KEY, JSON.stringify(kept));
  } catch {
    // private mode: the in-memory copy still covers this page
  }
};

const findStartedFlow = (nonce: string): OAuthFlow | undefined =>
  startedFlows.get(nonce) ??
  readStartedFlows().find((flow) => flow.nonce === nonce);

export const startedHere = (data: OAuthMessage): boolean =>
  isFlowResult(
    data,
    data.nonce === undefined ? null : findStartedFlow(data.nonce),
  );

export const broadcastResult = (
  status: OAuthStatus,
  message?: string | null,
  conversion?: AdPlatformConversion,
): void => {
  const payload: OAuthMessage = {
    ...readPopupFlow(),
    type: MSG_TYPE,
    status,
    message,
    conversion,
  };
  try {
    const channel = new BroadcastChannel(OAUTH_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // ignore
  }
  const opener = window.opener as Window | null;
  if (opener) {
    try {
      opener.postMessage(payload, window.location.origin);
    } catch {
      // ignore
    }
  }
};
